<template>
  <div>
    <!-- Header with title and filters -->
    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 px-6"
    >
      <h2 class="text-xl font-bold text-gray-900">
        <slot name="title">
          <Translation
            name="facilities.calculations.title"
            default="Living Wage Gap Calculations"
          />
        </slot>
      </h2>
      <div class="flex gap-3">
        <!-- Year Filter -->
        <select
          v-model="selectedYear"
          class="sm:w-[140px] pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white appearance-none bg-no-repeat bg-right"
          :class="selectedYear === 'all' ? 'text-gray-500' : 'text-gray-900'"
          style="
            background-image: url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3E%3Cpath stroke=%27%236B7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3E%3C/svg%3E');
            background-position: right 0.5rem center;
            background-size: 1.25rem 1.25rem;
          "
        >
          <option value="all">
            <Translation
              name="facilities.calculations.all_years"
              default="All Years"
            />
          </option>
          <option v-for="year in availableYears" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
        <!-- Status Filter -->
        <select
          v-model="selectedStatus"
          class="sm:w-[140px] pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white appearance-none bg-no-repeat bg-right"
          :class="selectedStatus === 'all' ? 'text-gray-500' : 'text-gray-900'"
          style="
            background-image: url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3E%3Cpath stroke=%27%236B7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3E%3C/svg%3E');
            background-position: right 0.5rem center;
            background-size: 1.25rem 1.25rem;
          "
        >
          <option value="all">
            <Translation
              name="facilities.calculations.all_statuses"
              default="All Statuses"
            />
          </option>
          <option value="draft">
            <Translation
              name="facilities.calculations.status_draft"
              default="Draft"
            />
          </option>
          <option value="submitted">
            <Translation
              name="facilities.calculations.status_submitted"
              default="Submitted"
            />
          </option>
          <option value="in_audit">
            <Translation
              name="facilities.calculations.status_in_audit"
              default="In Audit"
            />
          </option>
          <option value="archived">
            <Translation
              name="facilities.calculations.status_archived"
              default="Archived"
            />
          </option>
        </select>
      </div>
    </div>

    <!-- Desktop Table View -->
    <div
      v-if="filteredCalculations.length > 0"
      class="hidden md:block overflow-x-auto border-t border-gray-200"
    >
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              scope="col"
              class="w-[20%] px-6 py-3 text-left text-xs font-normal text-gray-500"
            >
              <Translation
                name="facilities.calculations.payroll_year"
                default="Payroll Year"
              />
            </th>
            <th
              scope="col"
              class="w-[20%] px-6 py-3 text-left text-xs font-normal text-gray-500"
            >
              <Translation
                name="facilities.calculations.calculation_id"
                default="ID"
              />
            </th>
            <th
              v-if="!legacy"
              scope="col"
              class="w-[20%] pl-2 pr-10 py-3 text-left text-xs font-normal text-gray-500"
            >
              <Translation
                name="facilities.calculations.progress"
                default="Progress"
              />
            </th>
            <th
              scope="col"
              class="px-6 py-3 text-left text-xs font-normal text-gray-500"
            >
              <Translation
                name="facilities.calculations.status"
                default="Status"
              />
            </th>
            <th
              scope="col"
              class="px-6 py-3 text-left text-xs font-normal text-gray-500"
            >
              <Translation
                name="facilities.calculations.last_updated"
                default="Last Updated"
              />
            </th>
            <th v-if="!legacy" scope="col" class="w-[50px] relative px-6 py-3">
              <span class="sr-only">
                <Translation
                  name="facilities.calculations.actions"
                  default="Actions"
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white">
          <tr
            v-for="calculation in paginatedCalculations"
            :key="calculation.id"
            class="hover:bg-[#F4FCFB] cursor-pointer border-b border-gray-200"
            @click="handleRowClick(calculation)"
          >
            <td
              class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-normal"
            >
              {{ calculation.reporting_year }}
            </td>
            <td
              class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-normal"
            >
              {{ calculation.id }}
            </td>
            <td v-if="!legacy" class="px-2 py-4 whitespace-nowrap">
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="h-2 rounded-full bg-success-300"
                  :style="{
                    width:
                      getProgressPercentage(calculation.frontend_status) + '%',
                  }"
                ></div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <span
                  class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full"
                  :class="getStatusClass(calculation.frontend_status)"
                >
                  {{ getStatusLabel(calculation.frontend_status) }}
                </span>
                <span
                  v-if="calculation.test"
                  class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full bg-amber-100 text-amber-800"
                >
                  {{ testLabel }}
                </span>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
              {{ formatDate(calculation.updated_at) }}
            </td>
            <td
              v-if="!legacy"
              class="px-6 py-4 whitespace-nowrap text-sm font-medium text-right"
            >
              <div class="relative dropdown-container">
                <button
                  :ref="(el) => setDropdownButtonRef(calculation.id, el)"
                  @click.stop="toggleDropdown(calculation.id, $event)"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <DotsVerticalIcon />
                </button>
                <Teleport to="body">
                  <div
                    v-if="openDropdownId === calculation.id"
                    :style="getDropdownStyles(calculation.id)"
                    class="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                  >
                    <button
                      v-for="action in getActions(calculation)"
                      :key="action.name"
                      @click.stop="handleAction(action.name, calculation.id)"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <component :is="action.icon" class="mr-3 h-4 w-4" />
                      <Translation v-bind.props="action.label" />
                    </button>
                  </div>
                </Teleport>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Card View -->
    <div v-if="filteredCalculations.length > 0" class="md:hidden space-y-4">
      <div
        v-for="calculation in paginatedCalculations"
        :key="calculation.id"
        @click="handleRowClick(calculation)"
        class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
      >
        <div class="flex items-start justify-between mb-3">
          <div class="text-base font-semibold text-gray-900">
            {{ calculation.reporting_year }}
          </div>
          <div
            v-if="!legacy"
            class="relative dropdown-container ml-3 flex-shrink-0"
          >
            <button
              :ref="(el) => setDropdownButtonRef(calculation.id, el)"
              @click.stop="toggleDropdown(calculation.id, $event)"
              class="text-gray-400 hover:text-gray-600"
            >
              <DotsVerticalIcon />
            </button>
            <Teleport to="body">
              <div
                v-if="openDropdownId === calculation.id"
                :style="getDropdownStyles(calculation.id)"
                class="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
              >
                <button
                  v-for="action in getActions(calculation)"
                  :key="action.name"
                  @click.stop="handleAction(action.name, calculation.id)"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <component :is="action.icon" class="mr-3 h-4 w-4" />
                  <Translation v-bind.props="action.label" />
                </button>
              </div>
            </Teleport>
          </div>
        </div>
        <div v-if="!legacy" class="mb-3">
          <div class="text-xs text-gray-500 mb-1.5">
            <Translation
              name="facilities.calculations.progress"
              default="Progress"
            />
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="h-2 rounded-full bg-success-300"
              :style="{
                width: getProgressPercentage(calculation.frontend_status) + '%',
              }"
            ></div>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span
              class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full"
              :class="getStatusClass(calculation.frontend_status)"
            >
              {{ getStatusLabel(calculation.frontend_status) }}
            </span>
            <span
              v-if="calculation.test"
              class="px-2.5 py-0.5 inline-flex text-sm font-medium rounded-full bg-amber-100 text-amber-800"
            >
              {{ testLabel }}
            </span>
          </div>
          <span class="text-sm text-gray-600">{{
            formatDate(calculation.updated_at)
          }}</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="filteredCalculations.length === 0" class="text-center py-12">
      <p class="text-base text-gray-500">
        <Translation
          name="facilities.calculations.no_calculations"
          default="No calculations found"
        />
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="filteredCalculations.length > 0" class="mt-4 px-6">
      <div class="text-sm text-gray-700 flex items-center gap-x-0.5">
        <Translation name="facilities.calculations.page" default="Page" />
        <span class="font-medium"> {{ currentPage }} </span>
        <Translation name="facilities.calculations.of" default="of" />
        <span class="font-medium">{{ totalPages }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, inject, Teleport } from "vue";
import DotsVerticalIcon from "./icons/DotsVerticalIcon.vue";
import EditIcon from "./icons/EditIcon.vue";
import TrashIcon from "./icons/TrashIcon.vue";
import CopyIcon from "./icons/CopyIcon.vue";
import { useTranslation } from "./aft/useTranslations";
import Translation from "./aft/Translation.vue";

export default {
  name: "FacilityCalculationsTable",
  components: { DotsVerticalIcon, Teleport, EditIcon, TrashIcon, Translation },
  props: {
    calculations: { type: Array, required: true },
    availableYears: { type: Array, default: () => [] },
    locale: { type: String, required: true },
    reportUrlTemplate: { type: String, required: true },
    wizardContinueUrlTemplate: { type: String, required: true },
    wizardFirstStepUrlTemplate: { type: String, required: true },
    archiveUrlTemplate: { type: String, required: true },
    reopenUrlTemplate: { type: String, required: true },
    copyUrlTemplate: {
      type: String,
      required: true,
    },
    legacy: { type: Boolean, default: false },
  },
  setup(props) {
    const selectedYear = ref("all");
    const selectedStatus = ref("all");
    const currentPage = ref(1);
    const perPage = 10;
    const openDropdownId = ref(null);
    const dropdownPosition = ref({});
    const dropdownButtonRefs = ref({});
    const { _t } = useTranslation();

    const filteredCalculations = computed(() => {
      let results = [...props.calculations];
      if (selectedYear.value !== "all") {
        results = results.filter(
          (c) => c.reporting_year === parseInt(selectedYear.value)
        );
      }
      if (selectedStatus.value !== "all") {
        results = results.filter(
          (c) => c.frontend_status[1] === selectedStatus.value
        );
      }
      return results;
    });

    const totalPages = computed(() =>
      Math.max(1, Math.ceil(filteredCalculations.value.length / perPage))
    );
    const paginatedCalculations = computed(() => {
      const start = (currentPage.value - 1) * perPage;
      return filteredCalculations.value.slice(start, start + perPage);
    });

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));
      if (diffDays === 0)
        return _t("facilities.calculations.today", "Today").value;
      if (diffDays === 1)
        return _t("facilities.calculations.yesterday", "1 day ago").value;
      if (diffDays < 7)
        return `${diffDays} ${
          _t("facilities.calculations.days_ago", "days ago").value
        }`;
      if (diffDays < 30)
        return `${Math.floor(diffDays / 7)} ${
          _t("facilities.calculations.weeks_ago", "weeks ago").value
        }`;
      return `${Math.floor(diffDays / 30)} ${
        _t("facilities.calculations.months_ago", "months ago").value
      }`;
    };

    const getStatusClass = (status) => {
      const classMap = {
        created: "bg-gray-100 text-gray-800",
        basic_info_completed: "bg-warning-200 text-warning-900",
        benchmark_completed: "bg-warning-200 text-warning-900",
        payroll_completed: "bg-warning-200 text-warning-900",
        draft: "bg-warning-200 text-warning-900", // frontend_status grouping
        submitted: "bg-success-200 text-green-800",
        in_audit: "bg-blue-200 text-blue-900",
        archived: "bg-gray-300 text-gray-600",
      };
      return classMap[status[0]] || "bg-gray-100 text-gray-800";
    };

    const getProgressPercentage = (status) => {
      const progressMap = {
        created: 0,
        basic_info_completed: 25,
        benchmark_completed: 50,
        payroll_completed: 75,
        draft: 50, // frontend_status grouping (average of draft statuses)
        submitted: 100,
        in_audit: 100,
        archived: 100,
      };
      return progressMap[status[0]] || 0;
    };

    const getStatusLabel = (status) => {
      const map = {
        draft: _t("facilities.calculations.status_draft", "Draft").value,
        submitted: _t("facilities.calculations.status_submitted", "Submitted")
          .value,
        in_audit: _t("facilities.calculations.status_in_audit", "In Audit")
          .value,
        archived: _t("facilities.calculations.status_archived", "Archived")
          .value,
      };
      return map[status[1]] || status;
    };

    const testLabel = _t("facilities.calculations.status_test", "Test");

    const isFinalized = (calc) =>
      ["submitted", "in_audit"].includes(calc.frontend_status[0]);

    const isArchived = (calc) => calc.frontend_status[0] === "archived";

    const getActions = (calculation) => {
      if (props.legacy) {
        return [];
      }

      const archiveOrRestore = isArchived(calculation)
        ? {
            name: "reopen",
            label: {
              name: "facilities.calculations.restore",
              default: "Restore",
            },
          }
        : {
            name: "archive",
            label: {
              name: "facilities.calculations.archive",
              default: "Archive",
            },
          };

      return [
        {
          name: "edit",
          label: isFinalized(calculation)
            ? {
                name: "facilities.calculations.reopen_to_edit",
                default: "Reopen to Edit",
              }
            : { name: "facilities.calculations.edit", default: "Edit" },
          icon: EditIcon,
          disabled: false,
        },
        {
          name: "copy",
          label: {
            name: "facilities.calculations.copy_calculation",
            default: "Copy Calculation",
          },
          icon: CopyIcon,
          disabled: false,
        },
        {
          name: archiveOrRestore.name,
          label: archiveOrRestore.label,
          icon: TrashIcon,
        },
      ];
    };

    const setDropdownButtonRef = (calculationId, el) => {
      if (el) {
        dropdownButtonRefs.value[calculationId] = el;
      }
    };

    const closeDropdown = () => {
      openDropdownId.value = null;
      dropdownPosition.value = {};
    };

    const toggleDropdown = (id, event) => {
      if (openDropdownId.value === id) {
        openDropdownId.value = null;
        dropdownPosition.value = {};
      } else {
        openDropdownId.value = id;
        if (event) {
          const button = event.currentTarget;
          const rect = button.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const dropdownHeight = 100;
          const spaceBelow = viewportHeight - rect.bottom;

          dropdownPosition.value[id] = {
            top: rect.bottom,
            right: window.innerWidth - rect.right,
            openUpward: spaceBelow < dropdownHeight,
            buttonBottom: rect.bottom,
            buttonTop: rect.top,
          };
        }
      }
    };

    const getDropdownStyles = (id) => {
      const position = dropdownPosition.value[id];
      if (!position) return {};

      const styles = {
        right: `${position.right}px`,
      };

      if (position.openUpward) {
        styles.bottom = `${window.innerHeight - position.buttonTop + 8}px`;
      } else {
        styles.top = `${position.buttonBottom + 8}px`;
      }

      return styles;
    };

    const handleScrollOrResize = () => {
      if (openDropdownId.value !== null) {
        closeDropdown();
      }
    };

    const handleRowClick = (calculation) => {
      const url = isFinalized(calculation)
        ? props.reportUrlTemplate.replace("__ID__", calculation.id)
        : props.wizardContinueUrlTemplate.replace("__ID__", calculation.id);
      window.location.href = url;
    };

    const handleAction = async (actionName, calculationId) => {
      openDropdownId.value = null;
      if (actionName === "edit") {
        const calculation = props.calculations.find(
          (c) => c.id === calculationId
        );

        if (calculation && isFinalized(calculation)) {
          // For finalized calculations, submit form to reopen endpoint (follows redirect)
          const reopenUrl = props.reopenUrlTemplate.replace(
            "__ID__",
            calculationId
          );

          const form = document.createElement("form");
          form.method = "POST";
          form.action = reopenUrl;

          const methodInput = document.createElement("input");
          methodInput.type = "hidden";
          methodInput.name = "_method";
          methodInput.value = "PATCH";
          form.appendChild(methodInput);

          const csrfToken = document.querySelector(
            'meta[name="csrf-token"]'
          ).content;
          const tokenInput = document.createElement("input");
          tokenInput.type = "hidden";
          tokenInput.name = "authenticity_token";
          tokenInput.value = csrfToken;
          form.appendChild(tokenInput);

          document.body.appendChild(form);
          form.submit();
        } else {
          const wizardUrl = props.wizardFirstStepUrlTemplate.replace(
            "__ID__",
            calculationId
          );
          window.location.href = wizardUrl;
        }
      } else if (actionName === "copy") {
        const calculation = props.calculations.find(
          (c) => c.id === calculationId
        );

        if (calculation) {
          const copyUrl = props.copyUrlTemplate.replace(
            "__ID__",
            calculationId
          );

          const form = document.createElement("form");
          form.method = "POST";
          form.action = copyUrl;

          const methodInput = document.createElement("input");
          methodInput.type = "hidden";
          methodInput.name = "_method";
          methodInput.value = "PATCH";
          form.appendChild(methodInput);

          const csrfToken = document.querySelector(
            'meta[name="csrf-token"]'
          ).content;
          const tokenInput = document.createElement("input");
          tokenInput.type = "hidden";
          tokenInput.name = "authenticity_token";
          tokenInput.value = csrfToken;
          form.appendChild(tokenInput);

          document.body.appendChild(form);
          form.submit();
        }
      } else if (actionName === "reopen") {
        const confirmMsg = _t(
          "facilities.calculations.confirm_restore",
          "Are you sure you want to restore this calculation?"
        ).value;
        if (!confirm(confirmMsg)) return;
        const reopenUrl = props.reopenUrlTemplate.replace(
          "__ID__",
          calculationId
        );
        const form = document.createElement("form");
        form.method = "POST";
        form.action = reopenUrl;
        const methodInput = document.createElement("input");
        methodInput.type = "hidden";
        methodInput.name = "_method";
        methodInput.value = "PATCH";
        form.appendChild(methodInput);
        const csrfToken = document.querySelector(
          'meta[name="csrf-token"]'
        ).content;
        const tokenInput = document.createElement("input");
        tokenInput.type = "hidden";
        tokenInput.name = "authenticity_token";
        tokenInput.value = csrfToken;
        form.appendChild(tokenInput);
        document.body.appendChild(form);
        form.submit();
      } else if (actionName === "archive") {
        const confirmMsg = _t(
          "facilities.calculations.confirm_archive",
          "Are you sure you want to archive this calculation?"
        ).value;

        if (!confirm(confirmMsg)) return;

        const url = props.archiveUrlTemplate.replace(
          "__ID__",
          calculationId
        );

        try {
          const response = await fetch(url, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
                .content,
            },
          });
          const data = await response.json();
          if (data.success) {
            const message = _t(
              "wage_calculations.archived_successfully",
              "Calculation archived successfully"
            ).value;
            alert(message);
            window.location.reload();
          } else {
            alert(
              data.message ||
                _t("facilities.calculations.action_error", "Action failed")
                  .value
            );
          }
        } catch (error) {
          console.error("Action error:", error);
          alert(
            _t("facilities.calculations.action_error", "Action failed").value
          );
        }
      }
    };

    const handleClickOutside = (event) => {
      if (openDropdownId.value !== null) {
        const dropdownElements = document.querySelectorAll(
          ".dropdown-container"
        );
        let clickedInside = false;

        dropdownElements.forEach((element) => {
          if (element.contains(event.target)) {
            clickedInside = true;
          }
        });

        if (!clickedInside) {
          closeDropdown();
        }
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    });

    return {
      selectedYear,
      selectedStatus,
      currentPage,
      totalPages,
      filteredCalculations,
      paginatedCalculations,
      formatDate,
      getStatusClass,
      getProgressPercentage,
      getStatusLabel,
      testLabel,
      getActions,
      setDropdownButtonRef,
      toggleDropdown,
      getDropdownStyles,
      openDropdownId,
      handleRowClick,
      handleAction,
    };
  },
};
</script>
